import React, { useState, useEffect } from 'react'
import Card from '../../components/Card.jsx'
import { todayISO } from '../../utils/dates.js'
import * as api from '../../services/api.js'

const ACTIVITIES = [
  { id: 'yoga',     label: 'Yoga',       emoji: '🧘', color: '#9E6EC8' },
  { id: 'run',      label: 'Running',    emoji: '🏃', color: '#238b45' },
  { id: 'dance',    label: 'Dancing',    emoji: '💃', color: '#C85070' },
  { id: 'swim',     label: 'Swimming',   emoji: '🏊', color: '#2A8A8A' },
  { id: 'cycle',    label: 'Cycling',    emoji: '🚴', color: '#7B4EAB' },
  { id: 'hike',     label: 'Hiking',     emoji: '🥾', color: '#A0722A' },
  { id: 'pilates',  label: 'Pilates',    emoji: '🤸', color: '#C49A4A' },
  { id: 'gym',      label: 'Gym',        emoji: '💪', color: '#6B3D8A' },
  { id: 'stretch',  label: 'Stretching', emoji: '🙆', color: '#52A96A' },
  { id: 'meditate', label: 'Meditation', emoji: '🪷', color: '#B48DC4' },
  { id: 'tennis',   label: 'Tennis',     emoji: '🎾', color: '#4a9e6e' },
  { id: 'sport',    label: 'Team Sport', emoji: '⚽', color: '#3D6B4F' },
]

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

function formatDisplay(isoDate) {
  const [y, m, d] = isoDate.split('-')
  return `${d}/${m}/${y}`
}

function migrate(entries) {
  if (!entries) return []
  return entries.map(e => {
    if (typeof e === 'string') return { id: e, mins: null, cals: null, link: '', doAgain: null }
    return { cals: null, link: '', doAgain: null, ...e }
  })
}

function buildCalendarDates() {
  const today = new Date()
  today.setHours(0, 0, 0, 0)
  // Monday = 0, ..., Sunday = 6
  const dow = today.getDay() === 0 ? 6 : today.getDay() - 1
  const start = new Date(today)
  start.setDate(start.getDate() - dow - 28)
  return Array.from({ length: 35 }, (_, i) => {
    const d = new Date(start)
    d.setDate(start.getDate() + i)
    return d.toISOString().slice(0, 10)
  })
}

function calcActivityStreak(log) {
  const dates = Object.keys(log)
    .filter(d => migrate(log[d]).length > 0)
    .sort()
    .reverse()
  if (!dates.length) return 0
  const today = new Date().toISOString().slice(0, 10)
  const yesterday = new Date(Date.now() - 86400000).toISOString().slice(0, 10)
  if (dates[0] !== today && dates[0] !== yesterday) return 0
  let streak = 1
  for (let i = 1; i < dates.length; i++) {
    const diff = Math.round(
      (new Date(dates[i - 1]) - new Date(dates[i])) / 86400000
    )
    if (diff === 1) streak++
    else break
  }
  return streak
}

const inputStyle = {
  padding: '5px 8px', border: '1px solid rgba(0,0,0,0.15)', borderRadius: '8px',
  background: 'var(--bg)', color: 'var(--ink)', fontSize: '0.82rem', fontFamily: 'inherit',
  width: '100%', boxSizing: 'border-box',
}

export default function ActivityTracker() {
  const [log, setLog]           = useState({})
  const [view, setView]         = useState('log')
  const [openForms, setOpenForms] = useState(new Set())

  useEffect(() => { api.getActivities().then(d => setLog(d || {})) }, [])

  const today = todayISO()
  const todayEntries = migrate(log[today] || [])
  const calDates = buildCalendarDates()
  const streak = calcActivityStreak(log)
  const totalDays = Object.keys(log).filter(d => migrate(log[d]).length > 0).length

  function updateLog(updater) {
    setLog(prev => {
      const next = updater(prev)
      api.saveActivities(next)
      return next
    })
  }

  function toggleActivity(id) {
    updateLog(prev => {
      const existing = migrate(prev[today] || [])
      const alreadyIn = existing.some(e => e.id === id)
      if (alreadyIn) {
        setOpenForms(s => { const n = new Set(s); n.delete(id); return n })
        return { ...prev, [today]: existing.filter(e => e.id !== id) }
      } else {
        setOpenForms(s => new Set([...s, id]))
        return { ...prev, [today]: [...existing, { id, mins: null, cals: null, link: '', doAgain: null }] }
      }
    })
  }

  function saveEntry(id) {
    setOpenForms(s => { const n = new Set(s); n.delete(id); return n })
  }

  function updateField(id, field, value) {
    updateLog(prev => {
      const existing = migrate(prev[today] || [])
      const updated = existing.map(e => e.id === id ? { ...e, [field]: value } : e)
      return { ...prev, [today]: updated }
    })
  }

  const counts = {}
  const totalMinsPerAct = {}
  const totalCalsPerAct = {}
  ACTIVITIES.forEach(a => { counts[a.id] = 0; totalMinsPerAct[a.id] = 0; totalCalsPerAct[a.id] = 0 })
  Object.values(log).forEach(raw => {
    migrate(raw).forEach(({ id, mins, cals }) => {
      if (counts[id] !== undefined) {
        counts[id]++
        totalMinsPerAct[id] += mins || 0
        totalCalsPerAct[id] += cals || 0
      }
    })
  })
  const totalSessions = Object.values(counts).reduce((s, n) => s + n, 0)
  const totalCalsAll = Object.values(totalCalsPerAct).reduce((s, n) => s + n, 0)
  const topActivity = ACTIVITIES.slice().sort((a, b) => counts[b.id] - counts[a.id])[0]

  return (
    <Card label="Check-in · Activities" title="Activity Tracker">

      {/* View tabs */}
      <div style={{ display: 'flex', gap: '0.4rem', marginBottom: '1.25rem' }}>
        {[['log', '📋 Log'], ['calendar', '📅 Calendar'], ['stats', '📊 Stats']].map(([v, label]) => (
          <button
            key={v}
            onClick={() => setView(v)}
            style={{
              padding: '5px 12px', borderRadius: '20px', border: '1px solid',
              borderColor: view === v ? 'var(--accent)' : 'rgba(0,0,0,0.15)',
              background: view === v ? 'var(--accent)' : 'transparent',
              color: view === v ? 'white' : 'var(--bark)',
              fontSize: '0.78rem', fontFamily: 'inherit', cursor: 'pointer',
              transition: 'all 0.18s',
            }}
          >
            {label}
          </button>
        ))}
      </div>

      {/* ── Log view ── */}
      {view === 'log' && (
        <>
          <p style={{ fontSize: '0.82rem', color: 'var(--stone)', marginBottom: '0.75rem' }}>
            Tap anything you did today ({formatDisplay(today)}).
          </p>

          <div className="act-btn-grid">
            {ACTIVITIES.map(act => {
              const selected = todayEntries.some(e => e.id === act.id)
              return (
                <button
                  key={act.id}
                  className={`act-btn ${selected ? 'selected' : ''}`}
                  style={{
                    borderColor: act.color,
                    color: selected ? 'white' : act.color,
                    background: selected ? act.color : 'transparent',
                  }}
                  onClick={() => toggleActivity(act.id)}
                >
                  {act.emoji} {act.label}
                </button>
              )
            })}
          </div>

          {todayEntries.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.75rem' }}>
              {todayEntries.map(entry => {
                const act = ACTIVITIES.find(a => a.id === entry.id)
                if (!act) return null

                /* ── Collapsed chip ── */
                if (!openForms.has(entry.id)) {
                  return (
                    <div key={entry.id} style={{
                      display: 'flex', alignItems: 'center', gap: '0.6rem',
                      background: 'var(--surf)', borderRadius: '10px',
                      padding: '0.55rem 0.85rem', borderLeft: `3px solid ${act.color}`,
                    }}>
                      <span style={{ color: act.color, fontWeight: 600, fontSize: '0.85rem', flexShrink: 0 }}>
                        {act.emoji} {act.label}
                      </span>
                      <div style={{ display: 'flex', gap: '0.4rem', flex: 1, flexWrap: 'wrap' }}>
                        {entry.mins > 0 && <span style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>{entry.mins} min</span>}
                        {entry.cals > 0 && <span style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>{entry.cals} kcal</span>}
                        {entry.doAgain === true  && <span style={{ fontSize: '0.7rem', color: 'var(--sage)' }}>✓ do again</span>}
                        {entry.doAgain === false && <span style={{ fontSize: '0.7rem', color: 'var(--stone)' }}>✗ skip next time</span>}
                      </div>
                      <button
                        onClick={() => setOpenForms(s => new Set([...s, entry.id]))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.72rem', color: 'var(--stone)', padding: '0 2px', flexShrink: 0 }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => toggleActivity(entry.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '0.7rem', color: 'var(--stone)', padding: '0 2px', flexShrink: 0 }}
                        title="Remove"
                      >
                        ✕
                      </button>
                    </div>
                  )
                }

                /* ── Open form ── */
                return (
                  <div key={entry.id} style={{
                    background: 'var(--surf)', borderRadius: '12px',
                    padding: '0.9rem 1rem', borderLeft: `3px solid ${act.color}`,
                  }}>
                    <p style={{
                      fontSize: '0.88rem', fontWeight: 600, color: act.color,
                      marginBottom: '0.65rem', display: 'flex', alignItems: 'center', gap: '0.4rem',
                    }}>
                      {act.emoji} {act.label}
                    </p>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.5rem', marginBottom: '0.5rem' }}>
                      <div>
                        <label style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--stone)', display: 'block', marginBottom: '3px' }}>
                          Duration (min)
                        </label>
                        <input
                          type="number" min="1" max="600" placeholder="—"
                          value={entry.mins ?? ''}
                          onChange={e => updateField(entry.id, 'mins', e.target.value ? parseInt(e.target.value, 10) : null)}
                          style={inputStyle}
                        />
                      </div>
                      <div>
                        <label style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--stone)', display: 'block', marginBottom: '3px' }}>
                          Calories (kcal)
                        </label>
                        <input
                          type="number" min="0" max="9999" placeholder="—"
                          value={entry.cals ?? ''}
                          onChange={e => updateField(entry.id, 'cals', e.target.value ? parseInt(e.target.value, 10) : null)}
                          style={inputStyle}
                        />
                      </div>
                    </div>

                    <div style={{ marginBottom: '0.5rem' }}>
                      <label style={{ fontSize: '0.65rem', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--stone)', display: 'block', marginBottom: '3px' }}>
                        Source link (optional)
                      </label>
                      <input
                        type="url" placeholder="https://..."
                        value={entry.link ?? ''}
                        onChange={e => updateField(entry.id, 'link', e.target.value)}
                        style={inputStyle}
                      />
                    </div>

                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.75rem' }}>
                      <span style={{ fontSize: '0.72rem', color: 'var(--stone)' }}>Do again?</span>
                      {[{ val: true, label: '✓ Yes' }, { val: false, label: '✗ No' }].map(({ val, label }) => (
                        <button
                          key={String(val)}
                          onClick={() => updateField(entry.id, 'doAgain', entry.doAgain === val ? null : val)}
                          style={{
                            padding: '3px 10px', borderRadius: '14px', fontSize: '0.72rem',
                            border: '1px solid',
                            borderColor: entry.doAgain === val ? (val ? '#52A96A' : '#C85070') : 'rgba(0,0,0,0.15)',
                            background: entry.doAgain === val ? (val ? '#52A96A' : '#C85070') : 'transparent',
                            color: entry.doAgain === val ? 'white' : 'var(--stone)',
                            cursor: 'pointer', fontFamily: 'inherit', transition: 'all 0.18s',
                          }}
                        >
                          {label}
                        </button>
                      ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <button
                        onClick={() => saveEntry(entry.id)}
                        style={{
                          background: 'var(--accent)', color: 'white', border: 'none',
                          borderRadius: '8px', padding: '6px 20px', fontSize: '0.82rem',
                          fontFamily: 'inherit', cursor: 'pointer', fontWeight: 600,
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )
              })}

              {/* Today summary line */}
              {(() => {
                const totalMinsToday = todayEntries.reduce((s, e) => s + (e.mins || 0), 0)
                const totalCalsToday = todayEntries.reduce((s, e) => s + (e.cals || 0), 0)
                return (
                  <p style={{ fontSize: '0.78rem', color: 'var(--bark)', fontStyle: 'italic', margin: 0 }}>
                    {todayEntries.length} activit{todayEntries.length === 1 ? 'y' : 'ies'} today
                    {totalMinsToday > 0 && ` · ${totalMinsToday} min`}
                    {totalCalsToday > 0 && ` · ${totalCalsToday} kcal`}
                  </p>
                )
              })()}
            </div>
          )}
        </>
      )}

      {/* ── Calendar view ── */}
      {view === 'calendar' && (
        <>
          <div className="act-calendar-header">
            {DAY_LABELS.map(d => (
              <div key={d} className="act-cal-day-label">{d}</div>
            ))}
          </div>

          <div className="act-calendar-grid">
            {calDates.map(date => {
              const acts = migrate(log[date] || [])
              const isToday = date === today
              const isFuture = date > today

              // Build hover tooltip
              const tooltipLines = [formatDisplay(date)]
              acts.forEach(e => {
                const a = ACTIVITIES.find(x => x.id === e.id)
                if (!a) return
                const parts = [a.label]
                if (e.mins) parts.push(`${e.mins} min`)
                if (e.cals) parts.push(`${e.cals} kcal`)
                tooltipLines.push(parts.join(' · '))
              })

              return (
                <div
                  key={date}
                  className={`act-cal-cell ${isToday ? 'today-cell' : ''} ${isFuture ? 'future-cell' : ''}`}
                  title={acts.length > 0 ? tooltipLines.join('\n') : formatDisplay(date)}
                >
                  <span className="act-cal-date">{Number(date.slice(8))}</span>

                  {!isFuture && acts.length > 0 && (
                    <div style={{ marginTop: '14px', width: '100%', display: 'flex', flexDirection: 'column', gap: '2px' }}>
                      {acts.map(e => {
                        const a = ACTIVITIES.find(x => x.id === e.id)
                        if (!a) return null
                        return (
                          <span key={e.id} style={{
                            fontSize: '0.46rem', fontWeight: 600, color: a.color,
                            whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                            lineHeight: 1.3, display: 'block',
                          }}>
                            {a.emoji} {a.label}
                          </span>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>

          {/* Colour legend */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem 0.75rem' }}>
            {ACTIVITIES.filter(a => counts[a.id] > 0).map(a => (
              <span key={a.id} style={{
                fontSize: '0.75rem', color: 'var(--bark)',
                display: 'flex', alignItems: 'center', gap: '5px',
              }}>
                <span style={{
                  width: 8, height: 8, borderRadius: '50%',
                  background: a.color, display: 'inline-block', flexShrink: 0,
                }} />
                {a.label}
              </span>
            ))}
          </div>
        </>
      )}

      {/* ── Stats view ── */}
      {view === 'stats' && (
        <>
          <div style={{ display: 'flex', gap: '0.75rem', marginBottom: '1.25rem', flexWrap: 'wrap' }}>
            {[
              { value: streak,        label: 'day streak',     emoji: '🔥' },
              { value: totalDays,     label: 'active days',    emoji: '📅' },
              { value: totalSessions, label: 'total sessions', emoji: '✅' },
              { value: totalCalsAll,  label: 'kcal burned',    emoji: '🔥' },
            ].map(s => (
              <div key={s.label} className="mission-stat-box" style={{ flex: 1, minWidth: '70px' }}>
                <div style={{ fontSize: '1.2rem' }}>{s.emoji}</div>
                <div className="mission-stat-value">{s.value}</div>
                <div className="mission-stat-label">{s.label}</div>
              </div>
            ))}
          </div>

          <p style={{
            fontSize: '0.68rem', textTransform: 'uppercase',
            letterSpacing: '0.1em', color: 'var(--stone)', marginBottom: '0.6rem',
          }}>
            Sessions by activity
          </p>
          <div className="act-stats-grid">
            {ACTIVITIES.map(act => (
              <div key={act.id} className="act-stat-card">
                <span className="act-stat-emoji">{act.emoji}</span>
                <span className="act-stat-count" style={{ color: act.color }}>
                  {counts[act.id]}
                </span>
                <span className="act-stat-label">{act.label}</span>
                {totalMinsPerAct[act.id] > 0 && (
                  <span style={{ fontSize: '0.6rem', color: 'var(--stone)', marginTop: '2px', display: 'block' }}>
                    {totalMinsPerAct[act.id]} min
                  </span>
                )}
                {totalCalsPerAct[act.id] > 0 && (
                  <span style={{ fontSize: '0.6rem', color: 'var(--stone)', display: 'block' }}>
                    {totalCalsPerAct[act.id]} kcal
                  </span>
                )}
              </div>
            ))}
          </div>

          {totalSessions === 0 && (
            <p style={{
              fontSize: '0.85rem', color: 'var(--stone)', fontStyle: 'italic',
              textAlign: 'center', marginTop: '1rem',
            }}>
              No activities logged yet — head to the Log tab to get started.
            </p>
          )}
          {totalSessions > 0 && topActivity && counts[topActivity.id] > 0 && (
            <p style={{
              fontSize: '0.82rem', color: 'var(--bark)', fontStyle: 'italic',
              textAlign: 'center', marginTop: '1rem',
            }}>
              Most frequent: {topActivity.emoji} {topActivity.label} — {counts[topActivity.id]} sessions
              {totalMinsPerAct[topActivity.id] > 0 && ` · ${totalMinsPerAct[topActivity.id]} min`}
              {totalCalsPerAct[topActivity.id] > 0 && ` · ${totalCalsPerAct[topActivity.id]} kcal`}
            </p>
          )}
        </>
      )}

    </Card>
  )
}
